import { MARKET_ASSETS, MARKET_SYMBOLS, type MarketSymbol } from '../data/market'
import type { TowerKind } from '../data/towers'
import { ENEMY_KINDS, type EnemyKind } from '../data/enemies'
import type { ProtocolId } from '../data/protocols'
import { gameBridge } from '../events/GameBridge'
import { metaProgress } from '../progression/MetaProgress'
import type { ContractId } from '../data/contracts'
import { emptyRunTelemetry, type RunTelemetry } from '../data/analytics'
import type { LoadoutId } from '../data/loadouts'
import { ELITE_IDS } from '../data/eliteModifiers'
import type { EnemyElite } from '../data/enemies'
import { FORMATIONS, type FormationId } from '../data/formations'
import { ENCOUNTER_DIRECTIVES, type EncounterDirectiveId } from '../data/encounters'
import { adjustedThreatLevel, campaignModifierFor, initialThreatLevel, type CampaignModifierId, type ThreatLevel, type ThreatTrend } from '../data/campaign'
import { getDailyChallenge, type RunMode } from '../data/endgame'
import { researchBonuses } from '../data/research'
import { playtestHistory } from '../analytics/PlaytestHistory'

export type GameStatus = 'PREPARING' | 'WAVE_ACTIVE' | 'PROTOCOL_SELECT' | 'VICTORY' | 'DEFEAT'
export type GameSpeed = 0 | 1 | 2

export interface MarketAssetSnapshot {
  readonly price: number
  readonly openPrice: number
  readonly changePercent: number
  readonly holdings: number
}

export interface MarketEventSnapshot {
  readonly headline: string
  readonly detail: string
  readonly tone: 'positive' | 'negative' | 'neutral'
  readonly remainingSeconds: number
}

export interface MarketSnapshot {
  readonly assets: Record<MarketSymbol, MarketAssetSnapshot>
  readonly gasGwei: number
  readonly portfolioValue: number
  readonly event: MarketEventSnapshot
}

export interface TowerModifiers {
  readonly damage: number
  readonly attackSpeed: number
  readonly range: number
}

export interface BossSnapshot {
  readonly name: string
  readonly hp: number
  readonly maxHp: number
  readonly phase: 1 | 2 | 3
  readonly phaseName: string
  readonly ability: string
  readonly color: number
}

export interface FormationSnapshot {
  readonly id: FormationId
  readonly name: string
  readonly glyph: string
  readonly description: string
  readonly color: number
  readonly groupSize: number
}

export interface GameSnapshot {
  readonly usdt: number
  readonly coreIntegrity: number
  readonly currentWave: number
  readonly totalWaves: number
  readonly runMode: RunMode
  readonly runSeed: number
  readonly status: GameStatus
  readonly nextWaveIn: number | null
  readonly incomingCount: number
  readonly mempool: Record<EnemyKind, number>
  readonly eliteMempool: Record<EnemyElite, number>
  readonly eliteIncomingCount: number
  readonly formation: FormationSnapshot
  readonly waveTitle: string
  readonly threatModifier: string
  readonly gameSpeed: GameSpeed
  readonly autoDeploy: boolean
  readonly earlyWaveBonusEarned: number
  readonly encounterDirective: EncounterDirectiveId
  readonly encounterLocked: boolean
  readonly encounterBonusEarned: number
  readonly campaignModifier: CampaignModifierId
  readonly threatLevel: ThreatLevel
  readonly threatTrend: ThreatTrend
  readonly activeContract: ContractId | null
  readonly activeLoadout: LoadoutId
  readonly runKills: number
  readonly telemetry: RunTelemetry
  readonly protocolChoices: readonly ProtocolId[]
  readonly activeProtocols: readonly ProtocolId[]
  readonly boss: BossSnapshot | null
  readonly market: MarketSnapshot
}

function initialMarket(startingCash = 10_000): MarketSnapshot {
  const assets = Object.fromEntries(MARKET_SYMBOLS.map((symbol) => {
    const price = MARKET_ASSETS[symbol].startingPrice
    return [symbol, { price, openPrice: price, changePercent: 0, holdings: 0 }]
  })) as Record<MarketSymbol, MarketAssetSnapshot>
  return {
    assets,
    gasGwei: 14,
    portfolioValue: startingCash,
    event: { headline: 'MARKET OPEN', detail: 'Price discovery initialized', tone: 'neutral', remainingSeconds: 8 },
  }
}

function initialState(): GameSnapshot {
  const profile = metaProgress.profile
  const challenge = getDailyChallenge()
  const runSeed = profile.selectedRunMode === 'DAILY_CHALLENGE' ? challenge.seed : profile.mapSeed
  const campaign = campaignModifierFor(profile.selectedMapId, runSeed)
  const research = researchBonuses(profile.level)
  const record = profile.sectorRecords[profile.selectedMapId]
  const map = metaProgress.selectedMap
  const baseThreatLevel = initialThreatLevel(profile.level, map.requiredLevel, record.clears, record.bestStars)
  const threatLevel = Math.min(3, baseThreatLevel + (profile.selectedRunMode === 'DAILY_CHALLENGE' ? 1 : 0)) as ThreatLevel
  const modeCapitalMultiplier = profile.selectedRunMode === 'DAILY_CHALLENGE' ? 0.9 : 1
  const usdt = Math.round((10_000 + metaProgress.startingCapitalBonus + research.startingCapital) * campaign.startingCapitalMultiplier * modeCapitalMultiplier)
  return {
    usdt,
    coreIntegrity: 100,
    currentWave: 1,
    totalWaves: profile.selectedRunMode === 'ENDLESS' ? 0 : 5,
    runMode: profile.selectedRunMode,
    runSeed,
    status: 'PREPARING',
    nextWaveIn: 7,
    incomingCount: 8,
    mempool: { BOT: 8, PAPER_HAND: 0, FUD_BOT: 0, LIQUIDATOR: 0, WHALE: 0 },
    eliteMempool: emptyEliteMempool(),
    eliteIncomingCount: 0,
    formation: FORMATIONS.DATA_STREAM,
    waveTitle: 'INITIALIZING SECTOR',
    threatModifier: 'SCANNING SIGNATURES',
    gameSpeed: 1,
    autoDeploy: false,
    earlyWaveBonusEarned: 0,
    encounterDirective: 'BALANCED_ROUTE',
    encounterLocked: false,
    encounterBonusEarned: 0,
    campaignModifier: campaign.id,
    threatLevel,
    threatTrend: 'STABLE',
    activeContract: null,
    activeLoadout: metaProgress.profile.preferredLoadout,
    runKills: 0,
    telemetry: { ...emptyRunTelemetry(), peakThreatLevel: threatLevel },
    protocolChoices: [],
    activeProtocols: [],
    boss: null,
    market: initialMarket(usdt),
  }
}

export class GameStore {
  private state: GameSnapshot = initialState()

  get snapshot(): GameSnapshot { return this.state }

  reset(): void { this.set(initialState()) }

  spend(amount: number): boolean {
    if (this.state.usdt < amount || this.isFinished()) return false
    this.setCash(this.state.usdt - amount)
    return true
  }

  reward(amount: number): void {
    if (!this.isFinished()) {
      const rewardMultiplier = (1 + this.protocolCount('LIQUIDITY_MINING') * 0.25) * researchBonuses(metaProgress.profile.level).killRewards
      this.setCash(this.state.usdt + Math.round(amount * rewardMultiplier))
    }
  }

  refund(amount: number): void {
    if (!this.isFinished()) this.setCash(this.state.usdt + amount)
  }

  grantEarlyWaveBonus(amount: number): void {
    if (amount <= 0 || this.isFinished()) return
    this.state = { ...this.state, earlyWaveBonusEarned: this.state.earlyWaveBonusEarned + amount }
    this.setCash(this.state.usdt + amount)
  }

  setGameSpeed(gameSpeed: GameSpeed): void {
    if (!this.isFinished()) this.set({ gameSpeed })
  }

  setAutoDeploy(autoDeploy: boolean): void {
    if (!this.isFinished()) this.set({ autoDeploy })
  }

  updateBoss(boss: BossSnapshot | null): void {
    this.set({ boss })
  }

  selectContract(activeContract: ContractId): boolean {
    if (this.state.status !== 'PREPARING' || this.state.currentWave !== 1) return false
    this.set({ activeContract })
    return true
  }

  selectLoadout(activeLoadout: LoadoutId): boolean {
    if (this.state.status !== 'PREPARING' || this.state.currentWave !== 1) return false
    metaProgress.selectLoadout(activeLoadout)
    this.set({ activeLoadout })
    return true
  }

  selectEncounter(encounterDirective: EncounterDirectiveId): boolean {
    if (this.state.status !== 'PREPARING' || this.state.encounterLocked) return false
    this.set({ encounterDirective })
    return true
  }

  lockEncounter(): void {
    if (!this.isFinished()) this.set({ encounterLocked: true })
  }

  prepareEncounter(): void {
    if (!this.isFinished()) this.set({ encounterDirective: 'BALANCED_ROUTE', encounterLocked: false })
  }

  completeEncounter(): number {
    if (this.isFinished()) return 0
    const directive = ENCOUNTER_DIRECTIVES[this.state.encounterDirective]
    const bonus = directive.clearBonus
    const telemetry = directive.id === 'BALANCED_ROUTE' ? this.state.telemetry : {
      ...this.state.telemetry,
      riskyEncountersCleared: this.state.telemetry.riskyEncountersCleared + 1,
      encounterBonus: this.state.telemetry.encounterBonus + bonus,
    }
    this.set({ usdt: this.state.usdt + bonus, encounterBonusEarned: this.state.encounterBonusEarned + bonus, telemetry })
    return bonus
  }

  adaptThreat(coreAtWaveStart: number): ThreatTrend {
    if (this.isFinished()) return 'STABLE'
    const next = adjustedThreatLevel(this.state.threatLevel, Math.max(0, coreAtWaveStart - this.state.coreIntegrity), this.state.coreIntegrity)
    const telemetry = {
      ...this.state.telemetry,
      peakThreatLevel: Math.max(this.state.telemetry.peakThreatLevel, next.level),
      threatEscalations: this.state.telemetry.threatEscalations + (next.trend === 'ESCALATED' ? 1 : 0),
    }
    this.set({ threatLevel: next.level, threatTrend: next.trend, telemetry })
    return next.trend
  }

  recordTowerShot(kind: TowerKind): void {
    if (this.isFinished()) return
    this.patchTelemetry({ towerShots: { ...this.state.telemetry.towerShots, [kind]: this.state.telemetry.towerShots[kind] + 1 } })
  }

  recordTowerDamage(kind: TowerKind, amount: number): void {
    if (this.isFinished()) return
    this.patchTelemetry({ towerDamage: { ...this.state.telemetry.towerDamage, [kind]: this.state.telemetry.towerDamage[kind] + amount } })
  }

  recordKill(enemyKind: EnemyKind, towerKind?: TowerKind, elite = false): void {
    if (this.isFinished()) return
    this.patchTelemetry({
      enemyKills: { ...this.state.telemetry.enemyKills, [enemyKind]: this.state.telemetry.enemyKills[enemyKind] + 1 },
      ...(towerKind ? { towerKills: { ...this.state.telemetry.towerKills, [towerKind]: this.state.telemetry.towerKills[towerKind] + 1 } } : {}),
      ...(elite ? { eliteKills: this.state.telemetry.eliteKills + 1 } : {}),
    })
    this.state = { ...this.state, runKills: this.state.runKills + 1 }
  }

  recordBreach(kind: EnemyKind, damage: number, elite = false): void {
    if (this.isFinished()) return
    this.patchTelemetry({
      breaches: { ...this.state.telemetry.breaches, [kind]: this.state.telemetry.breaches[kind] + 1 },
      coreDamageTaken: this.state.telemetry.coreDamageTaken + damage,
      ...(elite ? { eliteBreaches: this.state.telemetry.eliteBreaches + 1 } : {}),
    })
  }

  recordTowerBuilt(): void { this.incrementTelemetry('towersBuilt') }
  recordTowerUpgraded(): void { this.incrementTelemetry('towersUpgraded') }
  recordTowerRecycled(): void { this.incrementTelemetry('towersRecycled') }
  recordTowerAbility(kind: TowerKind): void {
    if (!this.isFinished()) this.patchTelemetry({ abilitiesUsed: { ...this.state.telemetry.abilitiesUsed, [kind]: this.state.telemetry.abilitiesUsed[kind] + 1 } })
  }

  private incrementTelemetry(field: 'towersBuilt' | 'towersUpgraded' | 'towersRecycled'): void {
    if (!this.isFinished()) this.patchTelemetry({ [field]: this.state.telemetry[field] + 1 })
  }

  private patchTelemetry(patch: Partial<RunTelemetry>): void {
    this.state = { ...this.state, telemetry: { ...this.state.telemetry, ...patch } }
  }

  damageCore(amount: number): void {
    if (this.isFinished()) return
    const coreIntegrity = Math.max(0, this.state.coreIntegrity - amount)
    this.set({ coreIntegrity, ...(coreIntegrity === 0 ? { status: 'DEFEAT' as const } : {}) })
    if (coreIntegrity === 0) {
      const result = metaProgress.completeRun(false, this.state.currentWave, coreIntegrity, this.state.runKills, this.state.earlyWaveBonusEarned, this.state.activeContract, this.state.telemetry)
      playtestHistory.record(result)
      gameBridge.emit('runCompleted', result)
      gameBridge.emit('audioCue', { cue: 'DEFEAT' })
    }
  }

  updateWave(fields: Pick<GameSnapshot, 'currentWave' | 'status' | 'nextWaveIn' | 'incomingCount' | 'mempool' | 'eliteMempool' | 'eliteIncomingCount' | 'formation' | 'waveTitle' | 'threatModifier'>): void {
    if (!this.isFinished()) this.set(fields)
  }

  setVictory(): void {
    if (this.isFinished()) return
    this.set({ status: 'VICTORY', nextWaveIn: null, incomingCount: 0, mempool: emptyMempool(), eliteMempool: emptyEliteMempool(), eliteIncomingCount: 0 })
    const result = metaProgress.completeRun(true, this.state.currentWave, this.state.coreIntegrity, this.state.runKills, this.state.earlyWaveBonusEarned, this.state.activeContract, this.state.telemetry)
    playtestHistory.record(result)
    gameBridge.emit('runCompleted', result)
    gameBridge.emit('audioCue', { cue: 'VICTORY' })
  }

  presentProtocols(choices: readonly ProtocolId[]): void {
    if (!this.isFinished()) this.set({ status: 'PROTOCOL_SELECT', protocolChoices: choices, nextWaveIn: null })
  }

  chooseProtocol(protocol: ProtocolId): boolean {
    if (!this.state.protocolChoices.includes(protocol) || this.state.status !== 'PROTOCOL_SELECT') return false
    const coreIntegrity = protocol === 'MARKET_SHIELD' ? Math.min(100, this.state.coreIntegrity + 15) : this.state.coreIntegrity
    const reserve = protocol === 'RESERVE_MINT' ? 750 : 0
    const usdt = this.state.usdt + reserve
    this.set({
      usdt,
      market: this.withPortfolioValue(this.state.market, usdt),
      coreIntegrity,
      activeProtocols: [...this.state.activeProtocols, protocol],
      protocolChoices: [],
    })
    return true
  }

  updateMarket(
    prices: Record<MarketSymbol, number>,
    gasGwei: number,
    event: MarketEventSnapshot,
  ): void {
    const assets = Object.fromEntries(MARKET_SYMBOLS.map((symbol) => {
      const current = this.state.market.assets[symbol]
      const price = prices[symbol]
      return [symbol, {
        ...current,
        price,
        changePercent: ((price - current.openPrice) / current.openPrice) * 100,
      }]
    })) as Record<MarketSymbol, MarketAssetSnapshot>
    this.set({ market: this.withPortfolioValue({ assets, gasGwei, event }) })
  }

  trade(symbol: MarketSymbol, side: 'BUY' | 'SELL', fraction: number): boolean {
    if (this.isFinished()) return false
    const asset = this.state.market.assets[symbol]
    const safeFraction = PhaserMathClamp(fraction, 0.01, 1)
    let nextCash = this.state.usdt
    let nextHoldings = asset.holdings
    if (side === 'BUY') {
      const budget = this.state.usdt * safeFraction
      if (budget < 1) return false
      nextCash -= budget
      nextHoldings += budget / asset.price
    } else {
      const quantity = asset.holdings * safeFraction
      if (quantity * asset.price < 0.01) return false
      nextHoldings -= quantity
      nextCash += quantity * asset.price
    }
    const assets = {
      ...this.state.market.assets,
      [symbol]: { ...asset, holdings: nextHoldings },
    }
    const market = this.withPortfolioValue({ ...this.state.market, assets }, nextCash)
    this.state = { ...this.state, usdt: nextCash, market }
    gameBridge.emit('stateChanged', this.state)
    return true
  }

  towerModifiers(kind: TowerKind): TowerModifiers {
    const asset = this.state.market.assets[kind]
    const positionValue = asset.holdings * asset.price
    const strength = Math.min(1, positionValue / 5_000)
    const protocolSpeed = 1 + this.protocolCount('NETWORK_BOOST') * 0.15
    const protocolDamage = 1 + this.protocolCount('EXECUTION_LAYER') * 0.12
    const protocolRange = 1 + this.protocolCount('ORACLE_ARRAY') * 0.12
    const research = researchBonuses(metaProgress.profile.level)
    if (kind === 'SOL') return { damage: protocolDamage * research.towerDamage, attackSpeed: (1 + strength * 0.35) * protocolSpeed * research.towerAttackSpeed, range: protocolRange * research.towerRange }
    if (kind === 'ETH') return { damage: (1 + strength * 0.15) * protocolDamage * research.towerDamage, attackSpeed: protocolSpeed * research.towerAttackSpeed, range: (1 + strength * 0.18) * protocolRange * research.towerRange }
    return { damage: (1 + strength * 0.4) * protocolDamage * research.towerDamage, attackSpeed: protocolSpeed * research.towerAttackSpeed, range: protocolRange * research.towerRange }
  }

  debugAddUsdt(amount = 1000): void { this.setCash(this.state.usdt + amount) }

  private setCash(usdt: number): void {
    this.set({ usdt, market: this.withPortfolioValue(this.state.market, usdt) })
  }

  private withPortfolioValue(market: Omit<MarketSnapshot, 'portfolioValue'> | MarketSnapshot, cash = this.state.usdt): MarketSnapshot {
    const holdingsValue = MARKET_SYMBOLS.reduce((total, symbol) => {
      const asset = market.assets[symbol]
      return total + asset.holdings * asset.price
    }, 0)
    return { ...market, portfolioValue: cash + holdingsValue }
  }

  private isFinished(): boolean { return this.state.status === 'VICTORY' || this.state.status === 'DEFEAT' }

  private protocolCount(protocol: ProtocolId): number {
    return this.state.activeProtocols.filter((active) => active === protocol).length
  }

  private set(patch: Partial<GameSnapshot>): void {
    this.state = { ...this.state, ...patch }
    gameBridge.emit('stateChanged', this.state)
  }
}

function emptyMempool(): Record<EnemyKind, number> {
  return Object.fromEntries(ENEMY_KINDS.map((kind) => [kind, 0])) as Record<EnemyKind, number>
}

function emptyEliteMempool(): Record<EnemyElite, number> {
  return Object.fromEntries(ELITE_IDS.map((id) => [id, 0])) as Record<EnemyElite, number>
}

function PhaserMathClamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export const gameStore = new GameStore()
